import type { Localized } from '../types'

const messages = {
  /** This catalogue's language. Server messages are English: they are shown only in 'en'. */
  lang: 'en',
  brand: {
    name: 'Galley',
    tagline: 'where students cook',
    home: 'Galley home',
  },
  nav: {
    skipToContent: 'Skip to main content',
    back: 'Back',
    help: 'Help',
    account: 'Account',
    /** Phones: one button that holds Help, Comfort and Account. */
    more: 'More',
    opensInNewTab: '(opens in a new tab)',
    yourClasses: 'Your classes',
    myClass: 'My class',
  },
  actions: {
    close: 'Close',
    tryAgain: 'Try again',
    reload: 'Reload the page',
    undo: 'Undo',
    dismiss: 'Dismiss',
    signOut: 'Sign out',
    goHome: 'Go to the home page',
  },
  status: {
    loading: 'Loading…',
    working: 'Working…',
    on: 'On',
    off: 'Off',
  },
  comfort: {
    button: 'Comfort',
    title: 'Comfort',
    intro: 'Language, motion, colors and reading style. They change Galley on this device only.',
    calm: { label: 'Calm motion', hint: 'Turns off movement and animation.' },
    night: { label: 'Night', hint: 'Darker colors for dim rooms.' },
    contrast: {
      label: 'High contrast',
      hint: 'Bold dark text on a plain light page.',
      fromDevice: 'On because your device asks for more contrast.',
    },
    reading: {
      label: 'Reading style',
      standard: 'Standard',
      spacious: 'Extra spacing',
      wide: 'Wide',
    },
    language: 'Language',
    followDevice: 'Use my device settings',
    projectorNote: 'The projector always uses calm motion and high contrast.',
  },
  languages: {
    en: 'English',
    es: 'Español',
  },
  errors: {
    network: "We couldn't reach Galley. Check your internet connection, then try again.",
    validation: "Some of that isn't right yet. Check it, then try again.",
    conflict: "That can't be done right now. Reload the page, then try again.",
    server: 'Galley had a problem on its side. Try again in a moment.',
    unauthorized: 'Please sign in again.',
    forbidden: "You don't have access to that.",
    notFound: "We couldn't find that. It may have been moved or deleted.",
    rateLimited: 'That was a lot of tries. Wait a minute, then try again.',
    locked: 'This is locked for now. Try again in a few minutes.',
    google: "We couldn't reach Google Classroom. Check that you're signed in, then try again.",
    generic: 'Something went wrong. Try again.',
    /**
     * By the server's error code (apps/server: conflict(), locked(), HttpError). Codes with
     * one message per code only; `validation`, `bad_state` and the like carry many different
     * messages and fall back to `validation` / `conflict` outside English.
     */
    byCode: {
      // Objects (family setup): a website's address, a 3D work's wall picture.
      site_private:
        'That address is inside a home or office network. Galley only saves sites on the public internet.',
      site_scheme: 'Only web addresses (starting with http or https) can be added.',
      site_credentials: 'Leave the name and password out of the address.',
      site_port: 'Galley only saves sites on the usual web ports.',
      site_bad_url: 'That doesn’t look like a web address.',
      still_unreadable: 'Galley couldn’t read that picture.',
      // The 3D walk saving where a visitor stands (the page never shows it: it waits and tries later).
      too_many_saves: 'Too many saves at once. Your place will be saved in a moment.',
      not_an_object: 'Only a 3D work or a website gets its wall picture this way.',
      nudge: 'Which part? Why? Say a bit more about what you noticed.',
      open_first: 'Open the work and look closely first.',
      kind_not_allowed: 'That kind of note isn’t needed at this stop.',
      walk_paused: 'Your teacher paused the walk. Wait for them to start it again.',
      notes_locked: 'Your teacher has locked notes for now.',
      walk_closed: 'This walk has ended.',
      unsend_expired: 'It’s too late to unsend this note.',
      too_late: 'This link was turned off. Make a new one instead.',
      passport_not_done: 'Finish your stops first.',
      none_left: 'You have visited every piece. Nice work!',
      not_returned: 'Your notes are not back yet.',
      use_one: 'Pick at least one note you’ll use.',
      piece_locked: 'Finish your stops first, then you can look around.',
      practice_first: 'Do the feedback practice first. It takes two minutes.',
      personal_info: 'Leave out links, emails, phone numbers and @names.',
      language: 'Please choose different words.',
      walk_active: 'Another walk is going in this class. End it first.',
      family_off: 'Turn on family links in More options first.',
      course_taken:
        'You made a new Galley for this class since. Delete that one to bring this one back.',
      no_course: 'This Galley isn’t linked to a Google Classroom class.',
      not_exhibitable:
        'Multiple-choice questions can’t be shown in a Galley. Pick an assignment or a short-answer question.',
      needs_drive: 'Galley needs your permission to open the files you pick in Google Drive.',
      demo: 'The demo class brings its files in without Google Drive.',
      demo_off: 'The demo is turned off on this Galley.',
      demo_busy: 'The demo is very busy right now. Try again in a little while.',
      demo_limit: 'Many demos were started from this network in the last hour. Try again later.',
      google_off: 'Google isn’t set up on this Galley yet. Ask the person who runs it.',
      google_reconnect: 'Galley lost access to your Google account. Reconnect to keep going.',
      classroom_disabled: 'Google Classroom is turned off for your account. Ask your tech team.',
      google_not_found: 'Google couldn’t find that. It may have been moved or deleted.',
      google_denied: 'Google says your account can’t open that.',
      google_busy: 'Google is busy right now. Try again in a minute.',
      google_too_large: 'That file is too big for Google to send. Try a smaller file.',
      ai_off: 'AI helpers are turned off. An admin can turn them on.',
      ai_failed: 'The AI helper is not available right now. You can write this yourself.',
      door_code: 'We couldn’t find that class code. Check it with your teacher.',
      wrong_pictures: 'Those pictures don’t match your card. Try again.',
      card_locked: 'Too many tries. Ask your teacher, or try again later.',
      card_unknown: 'This card doesn’t work any more. Ask your teacher for a new one.',
      link_expired: 'This link has expired or was turned off. Ask whoever shared it for a new one.',
      // Family setup (docs/museum-spec.md)
      office_locked: 'Enter the Office PIN first.',
      // A room's two lists (the round 9 plan §7)
      stale: 'This room changed while you were working on it. It’s up to date now; try that again.',
      updating: 'This room’s displays are still being worked out. Try again in a moment.',
      lists_preparing: 'The rooms are being set up with their displays. Try again in a moment.',
      fixed_order: 'A hallway is a timeline: its works always go by date.',
      nothing_to_undo: 'There is nothing to undo here now.',
      not_offered: 'That isn’t offered for this room.',
      display_not_addable: 'That display can’t be added to this room.',
      already_shown: 'That work is already on show.',
      kept_in_safe: 'That work is kept in the wall safe. Let it follow the lists first.',
      cannot_show: 'That work can’t be shown here: no display in this room takes it.',
      plan_needs_confirm:
        'Some rooms have displays and locks you set. Confirm these changes first.',
      one_order_change: 'Change the order one way at a time.',
      setup_family: 'This Galley is set up as a family museum.',
      setup_done: 'Galley is already set up.',
      sample_loading: 'The sample museum is still being hung. Try again when it’s done.',
      setup_claim:
        'Set Galley up on the computer it runs on, or enter the code shown in its window.',
      wrong_pin: 'That PIN is not right.',
      date_unreadable:
        'Galley couldn’t read that date. Try “1998”, “summer 2003”, “July 4, 2019” or “about 1995”.',
      floor_level_taken: 'There is already a floor at that level.',
      plan_invalid: 'Some rooms need fixing before the plan can be saved.',
      not_in_room: 'That work isn’t on display in this room.',
      not_on_display: 'Only a work on display can be featured at the entrance.',
      office_room: 'The Office has no walls to arrange.',
      archive_floor: 'The archive stays.',
      floor_has_entrance:
        'This floor has the lobby or the Office. Move them to another floor first.',
      // Several buildings (docs/site-buildings.md)
      buildings_changed: 'The buildings changed since you opened them. Have another look.',
      buildings_busy: 'Someone else is changing the buildings right now. Try again in a moment.',
      buildings_damaged:
        'The buildings can’t be read right now. Delete the damaged building, or restore a backup.',
      buildings_invalid: 'This building can’t be placed there yet.',
      building_has_office: 'This building has the Office. Move it to another building first.',
      main_building: 'The Main building stays where it is.',
      main_alone: 'Add another building first; then you can rename the Main building.',
      building_name_taken: 'Another building already has that name.',
      rooms_cut_off: 'Some rooms would be cut off. Move them first.',
      floors_not_empty:
        'A floor you’re taking off still has rooms. Delete that floor on its own first; its works go back to the archive.',
      building_match:
        'That doesn’t match where the building was dropped. Drag it onto the wall again.',
      storeys_locked_add:
        'A floor here would move the floors a wing is joined to. Add it at the top, or as a basement.',
      storeys_locked_level:
        'Changing this floor’s level would move the floors a wing is joined to.',
      storeys_locked_delete:
        'Deleting this floor would move the floors a wing is joined to. Delete the top floor instead, or leave this floor empty.',
      floor_beyond_building:
        'This floor would reach past the Main building. Make the building bigger too.',
      wing_on_wall:
        'A wing is joined to that wall. Move it to another wall in Buildings, or grow this floor the other way.',
      building_regridded:
        'This building was turned or joined since you opened the plan. Your last change wasn’t saved. Open the plan again to keep drawing.',
      lobby_annex: 'The lobby stays in the Main building.',
      annex_levels: 'This building’s floors are added at the top.',
      annex_size: 'This floor is as big as the wing. Change the wing’s size in Buildings.',
      annex_top_floor: 'Delete the building’s top floor first.',
      annex_last_floor: 'This is the building’s only floor. Delete the building instead.',
      annex_basement: 'This building can’t have a basement yet.',
      front_follows_lobby: 'The front door is the lobby’s. Move the lobby to move it.',
      gate_follows_front: 'The gate stays in line with the front door.',
      last_owner: 'The museum needs at least one owner.',
      pictures_resting:
        'Those pictures are resting after too many tries. Try again later, or sign in on the museum computer.',
      blocked:
        'The museum’s curator has turned off visits for this account. Ask them if you think that’s a mistake.',
      demo_no_files:
        'The sample museum can’t save backups or copies. Make your own museum to use this.',
      hub_not_paired:
        'This museum isn’t connected to the Curator’s Hub yet. Press Share anywhere first.',
      hub_not_set_up: 'The Curator’s Hub isn’t set up for this museum yet.',
      hub_unreachable:
        'Galley couldn’t reach the Curator’s Hub. Check that this computer is online, then try again.',
      job_running: 'That is already running. Wait for it to finish.',
      not_ready: 'This can’t be shown right now. Try again soon.',
      not_shared: 'This museum isn’t open to visitors from the internet.',
      home_sharing_off: 'Sharing at home is off. Turn it on in the Office on the museum computer.',
      pass_used:
        'This visit pass was already used. Go back to the Curator’s Hub and open the museum again.',
      pass_expired:
        'This visit pass has run out. Go back to the Curator’s Hub and open the museum again.',
      pass_invalid:
        'This visit pass isn’t valid. Go back to the Curator’s Hub and open the museum again.',
      pass_wrong_museum:
        'This visit pass is for a different museum. Go back to the Curator’s Hub and open the museum again.',
      csrf: 'Your session is out of date. Reload the page and try again.',
    },
  },
  boundary: {
    title: 'This page stopped working',
    body: 'Nothing you already sent is lost. Reload the page to keep going.',
  },
  session: {
    checking: 'Checking who you are…',
    teacherSignIn: 'Please sign in to see your classes.',
    studentSignIn: 'Please join your class first. Your teacher has the door code.',
    unreachableTitle: "We can't reach Galley right now",
    unreachableBody:
      'Check your internet connection. If it keeps happening, the server may be down.',
    /** /join and student pages when this browser also holds a teacher session. */
    teacherToo: 'You’re signed in as a teacher on this device.',
  },
  /** "See it as a student" in the demo: the student screens, with a way back. */
  preview: {
    seeing: (name: string) => `You’re seeing what ${name} sees (demo)`,
    back: 'Back to teacher view',
  },
  toast: {
    region: 'Notifications',
  },
  counter: {
    words: (n: number) => (n === 1 ? '1 word' : `${n} words`),
    wordsOfMin: (n: number, min: number) => `${n} of at least ${min} words`,
    chars: (n: number, max: number) => `${n} of ${max} characters`,
    charsLeft: (n: number) => (n === 1 ? '1 character left' : `${n} characters left`),
    overLimit: (n: number) => (n === 1 ? '1 character too many' : `${n} characters too many`),
  },
  notes: {
    glow: 'Glow',
    grow: 'Grow',
    wonder: 'Wonder',
    see: 'See',
    think: 'Think',
  },
  progress: {
    stops: (done: number, total: number) => `${done} of ${total} stops done`,
    stopDone: (n: number) => `Stop ${n}: done`,
    stopCurrent: (n: number) => `Stop ${n}: you are here`,
    stopTodo: (n: number) => `Stop ${n}: not yet`,
  },
  names: {
    you: 'you',
    hidden: 'Name hidden',
  },
  code: {
    label: 'Code',
  },
  placeholder: {
    comingTogether: 'Coming together',
    body: 'This screen is being built. The rest of Galley is ready to explore.',
  },
  flavor: {},
}

export type CommonMessages = Localized<typeof messages>
export const common: CommonMessages = messages
